import React, { useState, useEffect, memo } from 'react';
import { Card, Row, Col, Statistic, Table, Space, Button, Segmented, DatePicker, message, Skeleton, Spin } from 'antd';
import { MetricCardHeader } from './Shared/MetricCardHeader';
import { 
  LinkOutlined, 
  FileTextOutlined, 
  ExclamationCircleOutlined, 
  BookOutlined,
  ReloadOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { getCitationUsageAnalytics } from '../../api/api';
import type { CitationUsageResponse } from '../../types/api-models';
import dayjs, { Dayjs } from 'dayjs';

const { RangePicker } = DatePicker;

type AggregationType = 'daily' | 'weekly' | 'monthly';

interface CitationUsageAnalyticsProps {
  isExpanded: boolean;
  dashboardData?: any;
}

const CitationAnalysis: React.FC<CitationUsageAnalyticsProps> = memo(({ isExpanded, dashboardData }) => {
  const [data, setData] = useState<CitationUsageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  const [topCount, setTopCount] = useState(10);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await getCitationUsageAnalytics(
        'daily',
        dateRange[0].format('YYYY-MM-DD'),
        dateRange[1].format('YYYY-MM-DD')
      );
      setData(response);
    } catch (error) {
      console.error('Error fetching citation usage analytics:', error);
      message.error('Failed to load citation usage analytics');
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    // Only load data in collapsed view from dashboardData
    if (!isExpanded && dashboardData?.citation_usage) {
      setData(dashboardData.citation_usage);
    }
  }, [isExpanded, dashboardData]);

  const renderCollapsedView = () => {
    if (!data) return <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>No data available</div>;

    const { 
      citationUsageRate = 0, 
      averageCitationsPerAnswer = 0, 
      topCitedSources = [] 
    } = data || {};

    return (
      <div style={{ height: '100%' }}>
        <div style={{ marginBottom: 16 }}>
          <MetricCardHeader
            title="Citation Rate"
            value={`${Number(citationUsageRate.toFixed(1))}%`}
            trend={data.citation_trend}
            subtitle="Answers with citations"
          />
        </div>

        {/* Key Metrics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Statistic
              title="Avg per Answer"
              value={Number(averageCitationsPerAnswer.toFixed(1))}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: '16px' }}
            />
          </Col>
          <Col span={12}>
            <Statistic
              title="Total Citations"
              value={data.totalCitations || 0}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#faad14', fontSize: '16px' }}
            />
          </Col>
        </Row>

        {/* Top 5 Most Cited Sources */}
        <div style={{ marginBottom: 16 }}>
          <h4 style={{ marginBottom: 12, color: '#1890ff', fontSize: '14px' }}>Top Most Cited Sources</h4>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
            {(topCitedSources || []).slice(0, 3).map((source, index) => (
              <div key={index} style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                padding: '6px 8px',
                marginBottom: '4px',
                backgroundColor: index % 2 === 0 ? '#f5f5f5' : '#fff',
                borderRadius: '4px',
                border: '1px solid #e8e8e8',
                gap: '8px'
              }}>
                <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                  <a
                    href={source.source}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      fontSize: '12px', 
                      fontWeight: 500,
                      color: '#1890ff',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      display: 'block',
                      textDecoration: 'none'
                    }}
                    title={source.source}
                  >
                    {source.source}
                  </a>
                </div>
                <div style={{ 
                  fontSize: '12px', 
                  fontWeight: 600, 
                  color: '#52c41a',
                  flexShrink: 0,
                  textAlign: 'right'
                }}>
                  {source.citationCount}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderExpandedView = () => {
    return (
      <div style={{ height: '100%' }}>
        {/* Controls Row - Following Activity Trend Pattern */}
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
          <Space wrap>
            <span style={{ fontSize: 13, color: '#666' }}>Date Range:</span>
            <RangePicker
              value={dateRange}
              onChange={(dates) => {
                if (dates && dates[0] && dates[1]) {
                  const daysDiff = dates[1].diff(dates[0], 'days');
                  if (daysDiff > 90) {
                    message.warning('Date range cannot exceed 90 days');
                    return;
                  }
                  setDateRange([dates[0], dates[1]]);
                }
              }}
              format="YYYY-MM-DD"
              style={{ width: 260 }}
              disabledDate={(current) => current && current.isAfter(dayjs().endOf('day'))}
            />
            <span style={{ fontSize: 12, color: '#999', fontStyle: 'italic' }}>
              Max 90 days • Showing citation statistics for selected period
            </span>
          </Space>
          
          <Button type="primary" onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        </Space>

        {/* Chart */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <Spin size="large" tip="Loading citation usage analytics..." />
          </div>
        ) : !data ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#999' }}>
            <p style={{ fontSize: 16, marginBottom: 8 }}>Ready to view citation usage analytics</p>
            <p style={{ fontSize: 14 }}>Configure filters above and click "Apply Filters" to load data</p>
          </div>
        ) : (
          <>
            {/* Data content goes here */}
            {(() => {
              const { 
                citationUsageRate, 
                averageCitationsPerAnswer, 
                zeroCitationAnswers,
                totalCitations,
                uniqueCitations,
                topCitedSources,
                citationTrend
              } = data;
              return (
                <>
                  {/* Key Metrics */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col span={8}>
            <Statistic
              title="Citation Rate"
              value={Number(citationUsageRate.toFixed(1))}
              suffix="%"
              prefix={<LinkOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Avg Citations per Answer"
              value={Number(averageCitationsPerAnswer.toFixed(1))}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={8}>
            <Statistic
              title="Total Citations"
              value={totalCitations || 0}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
        </Row>

        {/* Top Cited Sources Table with Dropdown */}
        <Card 
          title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Top Cited Sources</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, color: '#666' }}>Show top:</span>
                <select 
                  value={topCount} 
                  onChange={(e) => setTopCount(Number(e.target.value))}
                  style={{ 
                    padding: '4px 8px', 
                    border: '1px solid #d9d9d9', 
                    borderRadius: '4px',
                    fontSize: '13px'
                  }}
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={15}>15</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
          } 
          style={{ marginBottom: 24 }}
        >
          <Table
            dataSource={(topCitedSources || []).slice(0, topCount)}
            columns={[
              {
                title: 'Source',
                dataIndex: 'source',
                key: 'source',
                ellipsis: true,
                width: '90%',
                render: (text: string) => (
                  <a
                    href={text}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ 
                      color: '#1890ff',
                      textDecoration: 'none'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                    title={text}
                  >
                    {text}
                  </a>
                ),
              },
              {
                title: 'Citation Count',
                dataIndex: 'citationCount',
                key: 'citationCount',
                sorter: (a, b) => a.citationCount - b.citationCount,
                defaultSortOrder: 'descend',
                width: '10%',
              },
            ]}
            pagination={false}
            size="small"
          />
        </Card>
                </>
              );
            })()}
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ height: '100%' }}>
      {isExpanded ? renderExpandedView() : renderCollapsedView()}
    </div>
  );
});

CitationAnalysis.displayName = 'CitationAnalysis';

export default CitationAnalysis;